//@flow
import React, {useState, useEffect} from 'react';
import {makeStyles, Typography, Divider} from '@material-ui/core';
import {colors} from '../../assets/styles/colors';

import imageBg from '../../assets/img/chat/whatsapp-bg.webp';
import {Storefront} from '@material-ui/icons';

import type {WhatsAppTemplate} from '../../entities/multichat/whatsapp-api';
import VariableSection from './VariableSection';
import MediaSection from './MediaSection';
import TemplateHeaderComponent from './TemplateHeaderComponent';
import TemplateBodyComponent from './TemplateBodyComponent';
import TemplateFooterComponent from './TemplateFooterComponent';
import TemplateButtonsComponent from './TemplateButtonsComponent';

// New type definitions for the secure variable system
type TemplateVariable = {
  id: string,
  scope: 'HEADER' | 'BODY',
  name?: string,
  position?: number,
  value: string,
};

type TemplateMedia = {
  id: string,
  type: 'IMAGE' | 'VIDEO' | 'DOCUMENT',
  scope: 'HEADER',
  value: string,
};

type Props = {
  template: WhatsAppTemplate,
  onChangeVariables: (variables: any) => void,
  enabledVariables?: boolean,
};

const useStyles = makeStyles(theme => ({
  container: {
    borderRadius: '8px',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    gap: '.5rem',

    [theme.breakpoints.up('sm')]: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
    },
    [theme.breakpoints.up('md')]: {
      gridTemplateColumns: '1.5fr 1fr',
    },
  },
  chatContainer: {
    display: 'flex',
    flexDirection: 'column',
    borderRadius: '.5rem',
    width: '100%',
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    padding: '16px',
    position: 'relative',
    width: '100%',
    height: '350px',
    overflow: 'auto',
    borderRadius: '0 0 8px 8px',
    background: `url(${imageBg}) no-repeat center center / cover`,
    justifyContent: 'flex-start',

    [theme.breakpoints.up('md')]: {
      height: '400px',
    },
  },
  background: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    zIndex: 0,
    objectFit: 'cover',
  },
  templateName: {
    fontSize: '12px',
    color: colors.grey600,
    maxWidth: '18ch',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    display: 'block',
  },
  messageContainer: {
    fontFamily: 'Arial, sans-serif',
    borderRadius: '8px',
    padding: '12px',
    boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
    backgroundColor: '#f9f9f9',
    position: 'relative',
    maxWidth: '250px',
    alignSelf: 'flex-start',
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',

    [theme.breakpoints.up('sm')]: {
      maxWidth: '300px',
    },

    [theme.breakpoints.up('md')]: {
      maxWidth: '350px',
    },
  },
  text: {
    fontSize: '12px',
    color: colors.grey900,

    [theme.breakpoints.up('md')]: {
      fontSize: '13px',
    },
  },
  buttons: {
    position: 'relative',
    display: 'flex',
    gap: '4px',
    width: '100%',
    flexWrap: 'wrap',
    maxWidth: '250px',
  },
  button: {
    color: colors.info,
    border: 'none',
    flexGrow: 1,
    borderRadius: '8px',
    padding: '8px 16px',
    cursor: 'pointer',
    fontSize: '14px',
    boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
    backgroundColor: '#f9f9f9',
  },
  header: {
    position: 'relative',
    backgroundColor: colors.grey200,
    padding: '8px',
    borderRadius: '.5rem .5rem 0 0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  headerChatInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  headerText: {
    fontSize: '12px',
    fontWeight: 600,
    color: colors.grey900,
  },
  icon: {
    width: '40px',
    height: '40px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.grey400,
    borderRadius: '50%',
    color: colors.grey800,
  },
  variablesSection: {
    padding: '16px',
    backgroundColor: '#f4f4f4',
    borderRadius: '8px',
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  inputSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  image: {
    width: '100%',
    borderRadius: '8px',
  },
}));

const extractTemplateVariables = (
  template: WhatsAppTemplate
): TemplateVariable[] => {
  const variables = [];

  template.components.forEach(component => {
    if (!component.text) return;
    if (component.type !== 'HEADER' && component.type !== 'BODY') return;
    const scope = component.type === 'HEADER' ? 'HEADER' : 'BODY';
    const textVariables = component.text.match(/{{[\w-]+}}/g) || [];

    const namedParams =
      component.type === 'HEADER'
        ? component?.example?.header_text_named_params || []
        : component?.example?.body_text_named_params || [];

    if (namedParams && namedParams.length > 0) {
      namedParams.forEach(param => {
        variables.push({
          id: crypto.randomUUID(),
          scope,
          name: param.param_name,
          value: param.example || '',
        });
      });
    } else {
      const examples =
        component.type === 'HEADER'
          ? component.example?.header_text || []
          : component.example?.body_text?.[0] || [];

      textVariables.forEach((variable, index) => {
        variables.push({
          id: crypto.randomUUID(),
          scope,
          position: index + 1,
          name: String(index + 1),
          value: examples[index] || '',
        });
      });
    }
  });

  return variables;
};

const extractTemplateMedia = (template: WhatsAppTemplate): TemplateMedia[] => {
  const media = [];

  const header = template.components.find(c => c.type === 'HEADER');
  if (header && ['IMAGE', 'VIDEO', 'DOCUMENT'].includes(header.format)) {
    const mediaValue = header.example?.header_handle?.[0] || '';
    media.push({
      id: crypto.randomUUID(),
      type: header.format,
      scope: 'HEADER',
      value: mediaValue,
    });
  }

  return media;
};

const buildPayload = (
  template: WhatsAppTemplate,
  variables: TemplateVariable[],
  media: TemplateMedia[]
) => {
  const payload = [];

  const bodyVars = variables.filter(v => v.scope === 'BODY');
  const headerVars = variables.filter(v => v.scope === 'HEADER');

  template.components.forEach(component => {
    if (component.type === 'HEADER') {
      const hasMedia = media.length > 0;
      if (hasMedia) {
        media.forEach(m => {
          const type = m.type.toLowerCase();

          const isLink = m.value.startsWith('https');

          payload.push({
            type: 'header',
            parameters: [
              {
                type,
                [type]: {
                  link: isLink ? m.value : undefined,
                  id: !isLink ? m.value : undefined,
                },
              },
            ],
          });
        });
        return;
      }

      if (headerVars.length === 0) return;

      const hasNamedParams =
        component?.example?.header_text_named_params?.length > 0;

      if (hasNamedParams) {
        payload.push({
          type: 'header',
          parameters: headerVars.map(v => ({
            type: 'text',
            parameter_name: v.name,
            text: v.value,
          })),
        });
      } else {
        payload.push({
          type: 'header',
          parameters: headerVars.map(v => ({
            type: 'text',
            text: v.value,
          })),
        });
      }
    }

    if (component.type === 'BODY') {
      if (bodyVars.length === 0) return;
      const hasNamedParams =
        component?.example?.body_text_named_params?.length > 0;
      if (hasNamedParams) {
        payload.push({
          type: 'body',
          parameters: bodyVars.map(v => ({
            type: 'text',
            parameter_name: v.name,
            text: v.value,
          })),
        });
      } else {
        payload.push({
          type: 'body',
          parameters: bodyVars.map(v => ({
            type: 'text',
            text: v.value,
          })),
        });
      }
    }
  });

  return payload;
};

export default function WhatsAppTemplateRenderer({
  template,
  enabledVariables,
  onChangeVariables,
}: Props) {
  const classes = useStyles();

  const [variables, setVariables] = useState<TemplateVariable[]>([]);
  const [media, setMedia] = useState<TemplateMedia[]>([]);

  const headerComponent = template.components.find(
    component => component.type === 'HEADER'
  );
  const bodyComponent = template.components.find(
    component => component.type === 'BODY'
  );
  const footerComponent = template.components.find(
    component => component.type === 'FOOTER'
  );
  const buttonsComponent = template.components.find(
    component => component.type === 'BUTTONS'
  );

  const updateVariable = (id: string, value: string) => {
    setVariables(prev => prev.map(v => (v.id === id ? {...v, value} : v)));
  };

  const updateMedia = (id: string, value: string) => {
    setMedia(prev => prev.map(m => (m.id === id ? {...m, value} : m)));
  };

  useEffect(() => {
    const extractedVariables = extractTemplateVariables(template);
    const extractedMedia = extractTemplateMedia(template);

    setVariables(extractedVariables);
    setMedia(extractedMedia);

    const payload = buildPayload(template, extractedVariables, extractedMedia);

    onChangeVariables(payload);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [template]);

  useEffect(() => {
    const payload = buildPayload(template, variables, media);
    console.log({payload});
    onChangeVariables(payload);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variables, media]);

  return (
    <div className={classes.container}>
      <section className={classes.chatContainer}>
        <header className={classes.header}>
          <div className={classes.headerChatInfo}>
            <span className={classes.icon}>
              <Storefront />
            </span>
            <div>
              <Typography className={classes.headerText}>
                {template.category}
              </Typography>
              <Typography variant="caption" className={classes.templateName}>
                {template.name}
              </Typography>
            </div>
          </div>
        </header>
        <div className={classes.content}>
          <div className={classes.messageContainer}>
            {headerComponent && (
              <TemplateHeaderComponent
                component={headerComponent}
                variables={variables}
                media={media}
              />
            )}
            {bodyComponent && (
              <TemplateBodyComponent
                component={bodyComponent}
                variables={variables}
              />
            )}
            {footerComponent && (
              <TemplateFooterComponent component={footerComponent} />
            )}
          </div>
          {buttonsComponent && (
            <TemplateButtonsComponent component={buttonsComponent} />
          )}
        </div>
      </section>

      {(variables.length > 0 || media.length > 0) && (
        <footer className={classes.variablesSection}>
          <Typography variant="subtitle2" style={{fontWeight: 'bold'}}>
            Editar Variables
          </Typography>
          <VariableSection
            title="Encabezado"
            templateText={headerComponent?.text}
            variables={variables.filter(v => v.scope === 'HEADER')}
            onVariableChange={updateVariable}
            enabledVariables={enabledVariables}
          />
          <MediaSection
            title="Encabezado"
            media={media}
            onMediaChange={updateMedia}
          />
          <Divider orientation="horizontal" />
          <VariableSection
            title="Cuerpo"
            templateText={bodyComponent?.text}
            variables={variables.filter(v => v.scope === 'BODY')}
            onVariableChange={updateVariable}
            enabledVariables={enabledVariables}
          />
        </footer>
      )}
    </div>
  );
}
